import React from 'react';

const ResumePrintLayout = ({ resumeItems = [], publications = [], settings = {} }) => {
  const experiences = resumeItems.filter(i => i.type === 'experience');
  const education = resumeItems.filter(i => i.type === 'education');
  const certificates = resumeItems.filter(i => i.type === 'certification');
  const awards = resumeItems.filter(i => i.type === 'achievement');
  const outreach = resumeItems.filter(i => ['outreach', 'speaking'].includes(i.type));

  const Th = ({ children, width }) => (
    <th className="px-4 py-2 font-bold text-left bg-slate-900 border border-slate-300 text-white" style={{ width }}>
      {children}
    </th>
  );

  const Td = ({ children }) => (
    <td className="px-4 py-2 border border-slate-300 text-black align-top break-words">
      {children}
    </td>
  );

  const renderBulletList = (jsonString) => {
    try {
      const arr = JSON.parse(jsonString || '[]');
      if (!Array.isArray(arr) || arr.length === 0) return null;
      return (
        <ul className="list-disc pl-4 mt-1 space-y-0.5 m-0 text-sm">
          {arr.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
    } catch {
      return null;
    }
  };

  return (
    <div className="hidden print:block w-full bg-white text-black p-8 font-sans">
      {/* Header */}
      <div className="text-center mb-6 pb-4 border-b-2 border-slate-900 flex justify-between items-end">
        <div className="text-left">
          <h1 className="text-4xl font-black text-slate-900 m-0 uppercase tracking-wide">Sudhan R</h1>
          <h2 className="text-xl font-bold text-slate-700 m-0 mt-1 uppercase">Computer Science Engineer & Researcher</h2>
        </div>
        <div className="text-right text-sm font-semibold text-slate-700">
          <p>sudhan.live</p>
          <p>{settings?.contact_email || 'sudhan.r.web@gmail.com'}</p>
          <p>{(settings?.social_linkedin || 'linkedin.com/in/sudhan23082004').replace(/https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</p>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Professional Summary</h3>
        <p className="text-sm font-medium leading-relaxed mb-0">
          Exploring space science through research, education, and innovation. Dedicated to data-driven discovery, geospatial intelligence,
          and artificial intelligence. Proven track record in research with Indian Space Research Organisation (ISRO) facilities, NASA STEM platforms, and academic distinction.
        </p>
      </div>

      {/* 1. Work Experience */}
      {experiences.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Experience & Internships</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="15%">Duration</Th>
                <Th width="30%">Role & Organization</Th>
                <Th width="55%">Key Highlights</Th>
              </tr>
            </thead>
            <tbody>
              {experiences.map((exp, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{exp.duration || exp.date_value}</Td>
                  <Td>
                    <div className="font-bold text-sm text-slate-900">{exp.title}</div>
                    <div className="font-semibold text-slate-700 mt-0.5">{exp.organization}</div>
                    {exp.location && <div className="text-xs text-slate-500 mt-0.5">{exp.location}</div>}
                  </Td>
                  <Td>
                    {exp.description && <div className="mb-1 text-sm font-medium">{exp.description}</div>}
                    {renderBulletList(exp.achievements)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 2. Education */}
      {education.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Education</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="15%">Year</Th>
                <Th width="45%">Degree / Program</Th>
                <Th width="30%">Institution</Th>
                <Th width="10%">Score</Th>
              </tr>
            </thead>
            <tbody>
              {education.map((edu, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{edu.duration || edu.date_value}</Td>
                  <Td>
                    <div className="font-bold text-slate-900">{edu.title}</div>
                  </Td>
                  <Td>{edu.organization}</Td>
                  <Td className="whitespace-nowrap font-bold text-center">
                     {edu.achievements ? (() => { try { return JSON.parse(edu.achievements)[0]?.split('CGPA')[0]?.trim() || ''; } catch { return ''; } })() : ''}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 3. Research & Publications */}
      {publications.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Research & Publications</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="10%">Year</Th>
                <Th width="45%">Publication Title</Th>
                <Th width="25%">Journal / Conference</Th>
                <Th width="20%">Authors</Th>
              </tr>
            </thead>
            <tbody>
              {publications.map((pub, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{pub.year}</Td>
                  <Td>
                    <div className="font-bold text-slate-900">{pub.title}</div>
                    <div className="text-xs text-slate-500 mt-1 uppercase font-semibold">{pub.status}</div>
                  </Td>
                  <Td>{pub.journal}</Td>
                  <Td>{pub.authors}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 4. Awards & Honors */}
      {awards.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Awards & Honors</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="12%">Date</Th>
                <Th width="40%">Award Title</Th>
                <Th width="30%">Issuing Organization</Th>
                <Th width="18%">Category</Th>
              </tr>
            </thead>
            <tbody>
              {awards.map((awd, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{awd.date_value}</Td>
                  <Td>
                    <div className="font-bold text-slate-900">{awd.title}</div>
                    {awd.description && <div className="text-xs text-slate-700 mt-1">{awd.description}</div>}
                  </Td>
                  <Td>{awd.organization}</Td>
                  <Td className="uppercase font-semibold text-xs tracking-wider">{awd.slug ? awd.slug.split('___')[0] : ''}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 5. Outreach & Speaking */}
      {outreach.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Outreach & Speaking Engagements</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="12%">Date</Th>
                <Th width="45%">Activity Title</Th>
                <Th width="43%">Description</Th>
              </tr>
            </thead>
            <tbody>
              {outreach.map((out, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{out.date_value}</Td>
                  <Td>
                    <div className="font-bold text-slate-900">{out.title}</div>
                    {out.organization && <div className="text-xs text-slate-700 font-semibold mt-0.5">{out.organization}</div>}
                  </Td>
                  <Td className="text-xs">{out.description}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 6. Certifications */}
      {certificates.length > 0 && (
        <div className="mb-6 block">
          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase border-l-4 border-slate-900 pl-2">Certifications</h3>
          <table className="w-full text-sm border-collapse bg-white">
            <thead>
              <tr className="bg-slate-900 text-white border border-slate-300">
                <Th width="15%">Date</Th>
                <Th width="50%">Certification Name</Th>
                <Th width="35%">Issuing Organization</Th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((cert, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <Td>{cert.date_value}</Td>
                  <Td>
                    <div className="font-bold text-slate-900">{cert.title}</div>
                  </Td>
                  <Td>{cert.organization}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer / Contact */}
      <div className="mt-12 pt-4 border-t-2 border-slate-900 text-center text-sm font-semibold text-slate-600">
        <p>For more detailed technical projects and interactive portfolio, please visit https://sudhan.live</p>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          @page {
            size: A4 auto;
            margin-top: 20mm !important;
            margin-bottom: 20mm !important;
            margin-left: 15mm !important;
            margin-right: 15mm !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-footer-group;
          }
          h3 {
            page-break-after: avoid;
          }
        }
      `}} />
    </div>
  );
};

export default ResumePrintLayout;
